// src/pages/youtube-insights/components/shared.ts
import { useMemo } from "react";
import { useAllVideos } from "@/hooks/mediaInsights/useMediaInsights";
import type { YouTubeVideoSummary } from "@/services/queries/mediaInsights/youtube";
import type { Channel } from "../types";
import type { IconName } from "lucide-react/dynamic";

// ============================================================================
// Category definitions
// ============================================================================
export type CategoryKey = "all" | "high-alpha" | "recent" | "market-analysis" | "technical-analysis" | "macro";

export interface CategoryDef {
  key: CategoryKey;
  label: string;
  icon: IconName;
  iconColor: string;
}

export const CATEGORIES: CategoryDef[] = [
  { key: "all", label: "All", icon: "layout-grid", iconColor: "text-gray-400" },
  { key: "high-alpha", label: "High Alpha", icon: "sparkles", iconColor: "text-amber-400" },
  { key: "recent", label: "Recent", icon: "clock", iconColor: "text-blue-400" },
  { key: "market-analysis", label: "Market Analysis", icon: "bar-chart-3", iconColor: "text-purple-400" },
  { key: "technical-analysis", label: "Technical Analysis", icon: "line-chart", iconColor: "text-cyan-400" },
  { key: "macro", label: "Macro & Institutional", icon: "building-2", iconColor: "text-orange-400" },
];

// ============================================================================
// Hook to load all videos (single API call)
// ============================================================================
export const useMultiChannelVideos = (channels: Channel[]) => {
  const { data, isLoading } = useAllVideos();
  
  // Create channel lookup map
  const channelMap = useMemo(() => {
    const map: Record<string, Channel> = {};
    channels.forEach(c => { map[c.id] = c; });
    return map;
  }, [channels]);

  // Map videos to include channel info
  const allVideos = useMemo(() => {
    if (!data?.data) return [];
    
    return data.data
      .map(video => {
        const channel = channelMap[video.channel_id];
        if (!channel) return null;
        return { video, channel };
      })
      .filter((item): item is { video: YouTubeVideoSummary; channel: Channel } => item !== null);
  }, [data, channelMap]);

  return { allVideos, isLoading: isLoading || channels.length === 0 };
};
